import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import EnhancedTable from 'components/dashboard/EnhancedTable';
import StatusBadge from 'components/dashboard/StatusBadge';
import ActionButtons from 'components/dashboard/ActionButtons';
import GlassyModal from 'components/ui/GlassyModal';
import CommentSection from 'components/dashboard/CommentSection';
import EditHistory from 'components/dashboard/EditHistory';
import AssignmentSubmissionForm from 'components/modals/AssignmentSubmissionForm';
import FloatingActionButton from 'components/ui/FloatingActionButton';
import Icon from 'components/AppIcon';
import { useAssignments } from 'hooks/useAssignments';
// Removed useProfiles import - clients don't need access to all user profiles
import { useAuth } from 'contexts/AuthContext';

const ClientAssignmentsTable = ({ onMessageClick }) => {
  const { user } = useAuth();
  // Use the filter to only get current client's assignments
  const { assignments, loading } = useAssignments({ clientId: user?.uid });
  // Removed useProfiles - clients don't need access to all user profiles

  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  // Status filter options for client
  const statusFilterOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'under-review', label: 'Under Review' },
    { value: 'revision-needed', label: 'Revision Needed' }
  ];

  // Helper function to get writer name - simplified for client view
  const getWriterName = (writerId) => {
    if (!writerId) return 'Not Assigned';
    return 'Assigned'; // Clients don't need to see specific writer names for privacy
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'assignment_title',
        header: 'Assignment Title',
        cell: ({ row }) => (
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={16} className="text-primary" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-text-primary">
                {row.original.assignment_title || 'Untitled Assignment'}
              </div>
              <div className="text-sm text-text-muted">
                {row.original.subject || 'No subject specified'}
              </div>
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'assignment_type',
        header: 'Type',
        cell: ({ row }) => (
          <span className="px-2 py-1 bg-accent/10 text-accent rounded-full text-sm">
            {row.original.assignment_type || 'Not specified'}
          </span>
        ),
      },
      {
        accessorKey: 'pages',
        header: 'Pages',
        cell: ({ row }) => (
          <span className="text-text-secondary">
            {row.original.pages || 'Not specified'}
          </span>
        ),
      },
      {
        accessorKey: 'deadline',
        header: 'Deadline',
        cell: ({ row }) => {
          const deadline = row.original.deadline;
          if (!deadline) return <span className="text-text-muted">No deadline set</span>;
          
          try {
            const date = deadline?.toDate ? deadline.toDate() : new Date(deadline);
            const isOverdue = date < new Date();
            
            return (
              <div className={`text-sm ${isOverdue ? 'text-error' : 'text-text-secondary'}`}>
                {format(date, 'MMM dd, yyyy')}
                {isOverdue && (
                  <div className="text-xs text-error">Overdue</div>
                )}
              </div>
            );
          } catch (error) {
            return <span className="text-text-muted">Invalid date</span>;
          }
        },
      },
      {
        accessorKey: 'assigned_writer',
        header: 'Writer',
        cell: ({ row }) => (
          <div className="text-text-secondary">
            {getWriterName(row.original.assigned_writer)}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge status={row.original.status || 'pending'} />
        ),
      },
    ],
    [] // Removed profiles dependency since we no longer use it
  );

  const handleViewDetails = (assignment) => {
    setSelectedAssignment(assignment);
    setShowDetailsModal(true);
  };

  const handleSubmissionSuccess = () => {
    setShowSubmissionModal(false);
    // Assignments will automatically refresh due to real-time listeners
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
            <Icon name="BookOpen" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">My Assignments</h2>
            <p className="text-text-muted text-sm">
              {assignments.length} assignment{assignments.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowSubmissionModal(true)}
          className="hidden md:flex bg-primary hover:bg-primary/80 text-white px-4 py-2 rounded-lg font-medium transition-colors items-center space-x-2"
        >
          <Icon name="Plus" size={16} />
          <span>Submit New Assignment</span>
        </button>
      </div>

      <EnhancedTable
        data={assignments}
        columns={columns}
        loading={loading}
        onRowClick={handleViewDetails}
        emptyMessage="No assignments found. Contact admin to create your first assignment."
        mobileCardLayout={true}
        mobileColumns={['assignment_title', 'assignment_type', 'deadline', 'pages']}
        responsiveBreakpoint="lg"
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        statusFilterOptions={statusFilterOptions}
        hideFiltersOnMobile={true}
        mobileActions={[
          {
            label: 'View Details',
            icon: 'Eye',
            onClick: handleViewDetails,
            className: 'bg-primary/30 hover:bg-primary/40 text-white font-medium'
          },
          {
            label: 'Message',
            icon: 'MessageSquare',
            onClick: (assignment) => {
              // Navigate to messages tab and open specific conversation
              onMessageClick && onMessageClick('assignment', assignment.id, assignment.assignment_title);
            },
            className: 'bg-white/20 hover:bg-white/30 text-white'
          }
        ]}
      />

      {/* Assignment Details Modal */}
      <GlassyModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedAssignment(null);
        }}
        title={selectedAssignment?.assignment_title || 'Assignment Details'}
        maxWidth="max-w-3xl"
      >
        {selectedAssignment && (
          <div className="space-y-6">
            {/* Assignment Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Subject</label>
                <p className="text-text-primary">{selectedAssignment.subject || 'Not specified'}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Type</label>
                <p className="text-text-primary">{selectedAssignment.assignment_type || 'Not specified'}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Pages</label>
                <p className="text-text-primary">{selectedAssignment.pages || 'Not specified'}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Status</label>
                <StatusBadge status={selectedAssignment.status || 'pending'} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Assigned Writer</label>
                <p className="text-text-primary">{getWriterName(selectedAssignment.assigned_writer)}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Deadline</label>
                <p className="text-text-primary">
                  {selectedAssignment.deadline ? 
                    format(selectedAssignment.deadline?.toDate ? selectedAssignment.deadline.toDate() : new Date(selectedAssignment.deadline), 'PPP') 
                    : 'Not set'
                  }
                </p>
              </div>
            </div>

            {/* Requirements */}
            {selectedAssignment.requirements && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Requirements</label>
                <div className="bg-surface p-4 rounded-lg">
                  <p className="text-text-primary whitespace-pre-wrap">{selectedAssignment.requirements}</p>
                </div>
              </div>
            )}

            {/* Additional Notes */}
            {selectedAssignment.additional_notes && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Additional Notes</label>
                <div className="bg-surface p-4 rounded-lg">
                  <p className="text-text-primary whitespace-pre-wrap">{selectedAssignment.additional_notes}</p>
                </div>
              </div>
            )}

            {/* QA Review Notes (if any) */}
            {selectedAssignment.qa_review_notes && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">QA Review Notes</label>
                <div className="bg-surface p-4 rounded-lg">
                  <p className="text-text-primary whitespace-pre-wrap">{selectedAssignment.qa_review_notes}</p>
                </div>
              </div>
            )}

            {/* Attachments */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-text-secondary">Your Attachments</label>
              {selectedAssignment.attachments && selectedAssignment.attachments.length > 0 ? (
                <div className="space-y-2">
                  {selectedAssignment.attachments.map((attachment, index) => (
                    <div key={index} className="bg-surface p-3 rounded-lg border border-border">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
                            <Icon name="FileText" size={14} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-text-primary font-medium">{attachment.name}</p>
                            <div className="text-xs text-text-muted">
                              {attachment.type && <span className="mr-3">{attachment.type}</span>}
                              {attachment.size && <span>{(attachment.size / 1024).toFixed(1)} KB</span>}
                            </div>
                          </div>
                        </div>
                        <a
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors"
                          title="View file"
                        >
                          <Icon name="Eye" size={14} className="text-primary" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 bg-surface rounded-lg border border-border">
                  <Icon name="FileX" size={32} className="text-text-muted mx-auto mb-2" />
                  <p className="text-text-muted">No attachments uploaded</p>
                </div>
              )}
            </div>

            {/* Comments Section */}
            <div className="border-t border-border pt-6">
              <CommentSection 
                itemId={selectedAssignment.id}
                itemType="assignment"
              />
            </div>

          </div>
        )}
      </GlassyModal>

      {/* Assignment Submission Modal */}
      <AssignmentSubmissionForm
        isOpen={showSubmissionModal}
        onClose={() => setShowSubmissionModal(false)}
        onSubmit={handleSubmissionSuccess}
      />

      {/* Mobile FAB */}
      <FloatingActionButton
        onClick={() => setShowSubmissionModal(true)}
        icon="Plus"
        label="Submit Assignment"
        position="bottom-right"
        size="md"
        showOnMobile={true}
        showOnDesktop={false}
      />
    </div>
  );
};

export default ClientAssignmentsTable;